import {
	ChangeDetectionStrategy,
	ChangeDetectorRef,
	Component,
	HostBinding,
	Input,
	Output,
	Pipe,
	PipeTransform,
	SkipSelf,
} from '@angular/core';
import { Subject } from 'rxjs';

@Pipe({
	name: 'dotClass',
	pure: true,
	standalone: true,
})
export class CarouselInstagramDotsClassPipe implements PipeTransform {
	transform(index: number, leftActiveIndex: number, rightActiveIndex: number): string {
		if (index < leftActiveIndex - 1 || index > rightActiveIndex + 1) {
			return 'tiny';
		} else if (index < leftActiveIndex || index > rightActiveIndex) {
			return 'small';
		}

		return '';
	}
}

@Component({
	selector: 'carousel-instagram-dots',
	templateUrl: './instagram-dots.component.html',
	styleUrls: ['./instagram-dots.component.scss'],
	changeDetection: ChangeDetectionStrategy.OnPush,
	standalone: true,
	imports: [CarouselInstagramDotsClassPipe],
})
export class CarouselInstagramDotsComponent {
	private _count = 0;

	/**
	 * The number of dots that the element has.
	 */
	@Input()
	public set count(count: number | undefined) {
		this._count = Number(count) || 0;
		this.dotsArray = Array.from(Array(this._count).keys());
		this.computeOffset();
	}

	public get count(): number {
		return this._count;
	}

	public dotsArray = Array.from(Array(this.count).keys());

	/**
	 * **Use the `index` setter instead of this property**.
	 */
	private _index = 0;

	/**
	 * The current active index.
	 */
	@Input()
	public set index(index: number | undefined) {
		this._index = Math.max(0, Math.min(this.count - 1, index || 0));
		this.computeOffset();
	}

	public get index(): number {
		return this._index;
	}

	@HostBinding('style.--__max-active-dots')
	@Input()
	public maxActiveCount = 5;

	@Output('dotClicked')
	public readonly dotClicked$$ = new Subject<number>();

	@HostBinding('style.--__offset')
	public offset = 0;

	public leftActiveIndex = 0;
	public rightActiveIndex = this.count;

	constructor(@SkipSelf() private readonly cdRef: ChangeDetectorRef) {}

	public dotsArrayTrackBy(_index: number, dotIndex: number): string {
		return `${dotIndex}`;
	}

	public indexSizeClass(index: number): string {
		return this.isTiny(index) ? 'tiny' : this.isSmall(index) ? 'small' : '';
	}

	public onDotClick(idx: number): void {
		this.dotClicked$$.next(idx);
	}

	private computeOffset(): void {
		const leftActiveIndex = this.offset;
		const rightActiveIndex = this.offset + this.maxActiveCount - 1;

		if (this.index < leftActiveIndex) {
			this.offset = this.index;
		} else if (this.index > rightActiveIndex) {
			this.offset = this.index - this.maxActiveCount + 1;
		}

		this.offset = Math.max(0, this.offset);

		this.leftActiveIndex = this.offset;
		this.rightActiveIndex = this.offset + this.maxActiveCount - 1;

		this.cdRef.detectChanges();
	}

	private isTiny(index: number): boolean {
		return index < this.offset - 1 || index > this.maxActiveCount + this.offset;
	}

	private isSmall(index: number): boolean {
		return index === this.offset - 1 || index === this.maxActiveCount + this.offset;
	}
}
